import { Link } from "react-router-dom";
import { ArrowRight, Drop, Sun, Sparkle, Leaf } from "@phosphor-icons/react";

const Skincare = () => {
  const productCategories = [
    {
      name: "Cleansers",
      icon: <Drop size={28} weight="light" />,
      products: [
        {
          name: "Gentle Clarifying Cleanser",
          description: "A soothing, pH-balanced formula that removes impurities without stripping moisture.",
          price: "$65",
        },
        {
          name: "Exfoliating Enzyme Wash",
          description: "Papaya and pineapple enzymes gently dissolve dead skin cells for radiant skin.",
          price: "$78",
        },
      ],
    },
    {
      name: "Serums",
      icon: <Sparkle size={28} weight="light" />,
      products: [
        {
          name: "Vitamin C Brightening Serum",
          description: "20% L-ascorbic acid formula for enhanced radiance and antioxidant protection.",
          price: "$145",
        },
        {
          name: "Hyaluronic Acid Complex",
          description: "Multi-weight hyaluronic acid for deep, lasting hydration at every skin level.",
          price: "$125",
        },
        {
          name: "Retinol Renewal Serum",
          description: "Encapsulated retinol for gentle yet effective anti-aging results.",
          price: "$165",
        },
      ],
    },
    {
      name: "Moisturizers",
      icon: <Leaf size={28} weight="light" />,
      products: [
        {
          name: "Barrier Repair Cream",
          description: "Rich ceramide complex that restores and protects the skin barrier.",
          price: "$95",
        },
        {
          name: "Lightweight Hydrating Gel",
          description: "Oil-free hydration perfect for combination and oily skin types.",
          price: "$85",
        },
      ],
    },
    {
      name: "Sun Protection",
      icon: <Sun size={28} weight="light" />,
      products: [
        {
          name: "Invisible Shield SPF 50",
          description: "Weightless mineral sunscreen with zero white cast for all skin tones.",
          price: "$55",
        },
        {
          name: "Tinted Defense SPF 40",
          description: "Light coverage with advanced UV protection and skin-perfecting pigments.",
          price: "$68",
        },
      ],
    },
  ];

  return (
    <div data-testid="skincare-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Medical-Grade Skincare
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-matte">
                Science
                <br />
                <span className="italic">Meets Skin</span>
              </h1>
              <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed">
                Our curated collection of medical-grade skincare products features 
                clinically proven ingredients at therapeutic concentrations. Each 
                product is selected to complement your aesthetic treatments and 
                maintain your results at home.
              </p>
              <Link
                to="/contact"
                data-testid="skincare-consult-btn"
                className="btn-primary inline-flex items-center gap-3 mt-10"
              >
                Book Skincare Consultation
                <ArrowRight size={16} weight="light" />
              </Link>
            </div>
            <div className="image-reveal aspect-square overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1621770522934-bfcc3b284df7?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Medical-grade skincare products"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16 md:mb-24">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Our Collection
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              Skincare Essentials
            </h2>
            <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
              Pharmaceutical-grade formulations designed to deliver real, visible results.
            </p>
          </div>

          {productCategories.map((category, categoryIndex) => (
            <div
              key={category.name}
              data-testid={`product-category-${categoryIndex}`}
              className="mb-16 last:mb-0"
            >
              <div className="flex items-center gap-4 mb-8">
                <span className="text-taupe">{category.icon}</span>
                <h3 className="font-serif text-2xl md:text-3xl font-light text-matte">
                  {category.name}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.products.map((product, productIndex) => (
                  <div
                    key={product.name}
                    data-testid={`product-${categoryIndex}-${productIndex}`}
                    className="product-card group"
                  >
                    <div className="flex-1 text-center">
                      <h4 className="font-serif text-lg text-matte mb-3">
                        {product.name}
                      </h4>
                      <p className="text-sm font-light text-matte/60 leading-relaxed">
                        {product.description}
                      </p>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-lg font-serif text-taupe">{product.price}</p>
                      <p className="text-xs text-matte/40 mt-2 uppercase tracking-wider">
                        Available in clinic
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skincare Protocol Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="grid grid-cols-2 gap-4">
              <div className="image-reveal aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1550572017-4b7a301b9d81?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Skincare serum bottle"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="image-reveal aspect-[3/4] overflow-hidden mt-12">
                <img
                  src="https://images.unsplash.com/photo-1593049258487-894b6252f629?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Skincare products collection"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Personalized Protocols
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
                Your Custom
                <br />
                Skincare Regimen
              </h2>
              <p className="mt-8 text-base font-light text-matte/70 leading-relaxed">
                During your skincare consultation, our aesthetic experts will analyze 
                your skin type, concerns, and goals to create a personalized protocol 
                that complements your in-clinic treatments.
              </p>
              <div className="mt-10 space-y-6">
                <div className="flex gap-4">
                  <span className="text-xl font-serif text-taupe">01</span>
                  <div>
                    <h4 className="font-serif text-lg text-matte mb-1">
                      Skin Analysis
                    </h4>
                    <p className="text-sm font-light text-matte/60">
                      Comprehensive assessment of your skin health and concerns.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="text-xl font-serif text-taupe">02</span>
                  <div>
                    <h4 className="font-serif text-lg text-matte mb-1">
                      Custom Protocol
                    </h4>
                    <p className="text-sm font-light text-matte/60">
                      Tailored morning and evening routines with product recommendations.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="text-xl font-serif text-taupe">03</span>
                  <div>
                    <h4 className="font-serif text-lg text-matte mb-1">
                      Ongoing Support
                    </h4>
                    <p className="text-sm font-light text-matte/60">
                      Regular check-ins and adjustments as your skin evolves.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
            Elevate Your Skincare
          </h2>
          <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
            Schedule a consultation to discover your personalized skincare protocol 
            and take home medical-grade products.
          </p>
          <Link
            to="/contact"
            data-testid="skincare-cta-btn"
            className="btn-primary inline-flex items-center gap-3 mt-10"
          >
            Book Consultation
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Skincare;
